import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../hooks/use-auth-context.hook';
import { pushAccountKey } from '../hooks/use-push-notifications.hook';
import AuthClientStore from '../services/auth-client.service';
import { resumePush } from '../services/web-push.service';

/**
 * Background half of Web Push, mounted once in App:
 *  - after sign-in, re-subscribes a browser whose account turned push on here
 *    before (sign-out drops the subscription), without prompting — and again
 *    when the interface language changes, so pushes arrive in it;
 *  - follows a clicked push notification's link inside the open tab.
 */
const PushSync = () => {
    const { i18n } = useTranslation();
    const { isAuthenticated, user, isSuperAdmin, sendAuthGuardedRequest } = useAuthContext();
    const accountKey = isAuthenticated ? pushAccountKey(user, isSuperAdmin) : null;
    const navigate = useNavigate();

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;
        const onMessage = (event: MessageEvent) => {
            if (event.data?.type === 'PUSH_NAVIGATE' && typeof event.data.route === 'string') {
                navigate(event.data.route);
            }
        };
        navigator.serviceWorker.addEventListener('message', onMessage);
        return () => navigator.serviceWorker.removeEventListener('message', onMessage);
    }, [navigate]);

    useEffect(() => {
        if (!accountKey || AuthClientStore.isSupportSession()) return;
        resumePush(sendAuthGuardedRequest, accountKey, i18n.language).catch((error) =>
            console.warn('Could not restore push notifications:', error),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountKey, i18n.language]);

    return null;
};

export default PushSync;
